import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send } from "lucide-react";

export default function Messages() {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [booking, setBooking] = useState(null);
  const bottomRef = useRef();

  const load = async () => {
    try {
      const [m, b] = await Promise.all([
        api.get(`/messages/${bookingId}`),
        api.get(`/bookings/${bookingId}`),
      ]);
      setMsgs(m.data);
      setBooking(b.data);
    } catch (err) {
      toast.error("Cannot load conversation");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [bookingId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    try {
      await api.post("/messages", { booking_id: bookingId, body });
      setBody("");
      load();
    } catch (err) { toast.error("Send failed"); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to={user?.role === "cook" ? "/cook" : "/owner"} className="tick-label">← Back</Link>
        {booking && (
          <div className="mt-4 border hairline p-5">
            <div className="tick-label">Booking</div>
            <h1 className="font-display text-3xl font-bold mt-1">{booking.kitchen_name}</h1>
            <div className="text-neutral-500 text-sm mt-1">{new Date(booking.start_time).toLocaleString()} · {booking.hours}h · ${booking.total.toFixed(2)}</div>
          </div>
        )}

        <div data-testid="messages-list" className="mt-6 border hairline p-5 h-[420px] overflow-y-auto space-y-3">
          {msgs.length === 0 ? (
            <div className="text-neutral-500 text-sm">No messages yet.</div>
          ) : msgs.map(m => (
            <div key={m.id} className={`flex ${m.sender_id === user?.id ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] px-4 py-2.5 border hairline ${m.sender_id === user?.id ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "bg-neutral-950"}`}>
                <div className="tick-label text-[10px] mb-1 opacity-70">{m.sender_name}</div>
                <div className="text-sm whitespace-pre-wrap">{m.body}</div>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        <form onSubmit={send} className="mt-4 flex gap-2">
          <input
            data-testid="message-input"
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 border hairline px-4 py-3 focus:outline-none focus:border-[#FF3B30]"
          />
          <button data-testid="message-send" type="submit" className="btn-signal flex items-center gap-2">
            <Send size={14} /> Send
          </button>
        </form>
      </div>
    </div>
  );
}
